import React, { useState, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { getFacturas } from "../api"
import { formatCurrency, formatNumber, severidadColor, rangoDelMes } from "../helpers"
import CopiarCufe from "../CopiarCufe"

const AlertaOperacional = ({ alerta }) => {
  const cfg = severidadColor(alerta.severidad)
  return (
    <div
      style={{
        background: cfg.bg, border: `1px solid ${cfg.border}`,
        borderLeft: `4px solid ${cfg.color}`,
        borderRadius: 10, padding: "14px 18px",
        display: "flex", alignItems: "flex-start", gap: 12,
      }}
    >
      <i className={`mdi mdi-${cfg.icon}`} style={{ fontSize: 22, color: cfg.color, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <span style={{
            background: cfg.color, color: "#fff",
            padding: "2px 8px", borderRadius: 4,
            fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
          }}>
            {alerta.severidad}
          </span>
          <span style={{
            background: "rgba(0,0,0,0.05)", padding: "2px 8px", borderRadius: 4,
            fontSize: 11, color: cfg.color, fontWeight: 600,
          }}>
            {alerta.tipo}
          </span>
          {alerta.metodo && (
            <span style={{ fontSize: 12, color: cfg.color, fontWeight: 500 }}>
              · {alerta.metodo}
            </span>
          )}
        </div>
        <div style={{ color: cfg.color, fontSize: 14, fontWeight: 600 }}>{alerta.mensaje}</div>
        {alerta.recomendacion && (
          <div style={{ color: cfg.color, fontSize: 12, marginTop: 6, opacity: 0.85 }}>
            <i className="mdi mdi-lightbulb-on-outline me-1" />
            {alerta.recomendacion}
          </div>
        )}
        {(alerta.valor_observado != null || alerta.umbral != null) && (
          <div style={{ color: cfg.color, fontSize: 11, marginTop: 6, opacity: 0.7 }}>
            Observado: <strong>{alerta.valor_observado}</strong> · Umbral: <strong>{alerta.umbral}</strong>
          </div>
        )}
      </div>
    </div>
  )
}

AlertaOperacional.propTypes = { alerta: PropTypes.object }

const TIPO_COMPROBANTE = {
  "01": "Factura",
  "02": "Nota débito",
  "03": "Nota crédito",
  "05": "Doc. equivalente",
  "91": "Nota crédito",
  "92": "Nota débito",
  "95": "Doc. equivalente",
}

const TIPO_INCONSISTENCIA = {
  metodo_faltante:  { color: "#f59e0b", icon: "minus-circle-outline", label: "Falta método" },
  metodo_sobrante:  { color: "#3b82f6", icon: "plus-circle-outline",  label: "Sobra método" },
  exceso_pago:      { color: "#dc2626", icon: "cash-plus",            label: "Exceso de pago" },
}

const Inconsistencia = ({ inc }) => {
  const tipoCfg = TIPO_INCONSISTENCIA[inc.tipo]
    || { color: "#ef4444", icon: "alert-circle-outline", label: inc.tipo || "Inconsistencia" }

  const folioCompleto = inc.prefijo ? `${inc.prefijo}-${inc.folio}` : `${inc.folio}`
  const tipoCompLabel = TIPO_COMPROBANTE[inc.tipo_comprobante] || inc.tipo_comprobante

  return (
    <div style={{
      background: "#fff", border: "1px solid #e2e8f0",
      borderLeft: `4px solid ${tipoCfg.color}`,
      borderRadius: 10, padding: "12px 16px",
    }}>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span style={{
            background: tipoCfg.color + "22", color: tipoCfg.color,
            padding: "2px 8px", borderRadius: 4,
            fontSize: 11, fontWeight: 700, textTransform: "uppercase",
          }}>
            <i className={`mdi mdi-${tipoCfg.icon} me-1`} />
            {tipoCfg.label}
          </span>
          {tipoCompLabel && (
            <span style={{
              background: "#f1f5f9", color: "#475569",
              padding: "2px 8px", borderRadius: 4,
              fontSize: 11, fontWeight: 600,
            }}
            title={`Tipo comprobante: ${inc.tipo_comprobante}`}
            >
              {tipoCompLabel}
            </span>
          )}
        </div>
        <span style={{
          fontSize: 12, color: "#0f172a", fontWeight: 600,
          fontFamily: "ui-monospace, SFMono-Regular, monospace",
        }}>
          {folioCompleto}
        </span>
      </div>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span>
          <i className="mdi mdi-store text-primary me-1" />
          {inc.sucursal} · {inc.fecha}
        </span>
        <CopiarCufe cufe={inc.cufe} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, fontSize: 12 }}>
        <div>
          <div style={{ color: "#94a3b8", textTransform: "uppercase", fontSize: 10, fontWeight: 600 }}>Total factura</div>
          <div style={{ color: "#0f172a", fontWeight: 700 }}>{formatCurrency(inc.total_factura)}</div>
        </div>
        <div>
          <div style={{ color: "#94a3b8", textTransform: "uppercase", fontSize: 10, fontWeight: 600 }}>Suma métodos</div>
          <div style={{ color: "#0f172a", fontWeight: 700 }}>{formatCurrency(inc.suma_metodos)}</div>
        </div>
        <div>
          <div style={{ color: "#94a3b8", textTransform: "uppercase", fontSize: 10, fontWeight: 600 }}>Diferencia</div>
          <div style={{ color: tipoCfg.color, fontWeight: 700 }}>{formatCurrency(inc.diferencia)}</div>
        </div>
      </div>
    </div>
  )
}

Inconsistencia.propTypes = { inc: PropTypes.object }

const AlertasTab = ({ alertasOperacionales, filtros }) => {
  const [inconsistencias, setInconsistencias] = useState([])
  const [loadingInc, setLoadingInc] = useState(false)
  const [errorInc, setErrorInc] = useState("")

  const cargarInconsistencias = useCallback(async () => {
    if (!filtros?.nitemisor) return
    setLoadingInc(true)
    setErrorInc("")
    try {
      // index.jsx inyecta fecha_inicio/fecha_fin tanto en modo mes como en rango.
      const fecha_inicio = filtros.fecha_inicio || rangoDelMes(filtros.year, filtros.month).fecha_inicio
      const fecha_fin = filtros.fecha_fin || rangoDelMes(filtros.year, filtros.month).fecha_fin
      const { data } = await getFacturas({
        nitemisor: filtros.nitemisor,
        fecha_inicio, fecha_fin,
        sucursal: filtros.sucursal,
        tipo_metodo: filtros.tipo_metodo,
        campo_fecha: filtros.campo_fecha,
        limit: 500, offset: 0,
        incluir: "inconsistencias",
      })
      setInconsistencias(data?.inconsistencias || [])
    } catch (err) {
      setErrorInc(err.response?.data?.message || err.message || "No se pudieron cargar inconsistencias.")
    } finally {
      setLoadingInc(false)
    }
  }, [filtros])

  useEffect(() => { cargarInconsistencias() }, [cargarInconsistencias])

  const opCount = (alertasOperacionales || []).length
  const incCount = inconsistencias.length

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Alertas operacionales */}
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-alert-octagon-outline me-2 text-warning" />
            Alertas operacionales ({opCount})
          </h6>
        </div>
        {opCount === 0 ? (
          <div style={{ background: "#d1fae5", color: "#065f46", padding: "14px 18px", borderRadius: 10, border: "1px solid #a7f3d0" }}>
            <i className="mdi mdi-check-circle me-2" />
            Sin alertas operacionales en el período consultado.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {alertasOperacionales.map((a, i) => <AlertaOperacional key={i} alerta={a} />)}
          </div>
        )}
      </div>

      {/* Inconsistencias de facturas */}
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-receipt-text-remove-outline me-2 text-danger" />
            Inconsistencias en facturas ({formatNumber(incCount)})
            <span style={{ fontSize: 11, fontWeight: 400, color: "#64748b", marginLeft: 8 }}>
              (regla 3 — sin ajuste, requieren revisión manual)
            </span>
          </h6>
        </div>

        {errorInc && (
          <div style={{ padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 8 }}>
            <i className="mdi mdi-alert-circle me-2" />
            {errorInc}
          </div>
        )}

        {loadingInc ? (
          <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
            <Spinner color="primary" /> <div className="mt-2">Cargando inconsistencias…</div>
          </div>
        ) : incCount === 0 && !errorInc ? (
          <div style={{ background: "#d1fae5", color: "#065f46", padding: "14px 18px", borderRadius: 10, border: "1px solid #a7f3d0" }}>
            <i className="mdi mdi-check-circle me-2" />
            Sin inconsistencias detectadas. Todas las facturas se reconciliaron con las reglas 1 ó 2.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 10 }}>
            {inconsistencias.map((inc, i) => <Inconsistencia key={i} inc={inc} />)}
          </div>
        )}
      </div>
    </div>
  )
}

AlertasTab.propTypes = {
  alertasOperacionales: PropTypes.array,
  filtros: PropTypes.object,
}

export default AlertasTab
